import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useFirebase } from '../contexts/FirebaseContext';
import Post from '../components/Post';

interface PostType {
  id: string;
  content: string;
  author: {
    id: string;
    username: string;
    displayName: string;
    avatar?: string;
  };
  likes: string[];
  retweets: string[];
  comments: Array<{
    id: string;
    content: string;
    author: {
      id: string;
      username: string;
      displayName: string;
      avatar?: string;
    };
    likes: string[];
    media: Array<{
      url: string;
      contentType: 'image' | 'video' | 'gif';
    }>;
    createdAt: string;
  }>;
  media: Array<{
    url: string;
    contentType: 'image' | 'video' | 'gif';
  }>;
  createdAt: string;
  isBookmarked: boolean;
}

const Bookmarks: React.FC = () => {
  const [bookmarkedPosts, setBookmarkedPosts] = useState<PostType[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useFirebase();

  useEffect(() => {
    const fetchBookmarkedPosts = async () => {
      if (!currentUser) return;

      try {
        // Get user's bookmarks
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        const bookmarkIds = userData?.bookmarks || [];

        if (bookmarkIds.length === 0) {
          setBookmarkedPosts([]);
          setLoading(false);
          return;
        }

        // Get all posts that are bookmarked
        const postsRef = collection(db, 'posts');
        const postsQuery = query(postsRef, where('__name__', 'in', bookmarkIds));
        const postsSnapshot = await getDocs(postsQuery);

        const posts = postsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          isBookmarked: true // Since these are bookmarked posts
        }));

        setBookmarkedPosts(posts);
      } catch (error) {
        console.error('Error fetching bookmarked posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookmarkedPosts();
  }, [currentUser]);

  if (!currentUser) return null;

  return (
    <div>
      <div className="border-b border-gray-800 sticky top-0 bg-gray-900/80 backdrop-blur-sm z-10">
        <h1 className="text-xl font-bold p-4">Bookmarks</h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
        </div>
      ) : bookmarkedPosts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg">No bookmarked posts</p>
          <p className="text-sm">When you bookmark posts, you'll see them here</p>
        </div>
      ) : (
        <div>
          {bookmarkedPosts.map((post) => (
            <Post
              key={post.id}
              post={post}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Bookmarks;